import { ActionIcon, Button, NumberInput, Select } from '@mantine/core';
import { X } from '@phosphor-icons/react';
import React from 'react';

import type { BomMaterialPick } from '@api/bom-management';
import SaveState, { type SaveStatus } from '@components/ui/SaveState';
import SegmentedToggle from '@components/ui/SegmentedToggle';
import StatusPill from '@components/ui/StatusPill';
import { formatMaterialQuantity } from '@utils/materialQuantity';

import { materialOptions, type BomLineDraft } from './bomOptions';
import { FULL_WIDTH_DROPDOWN, type SelectOption } from './makerOptions';
import styles from './BomRows.module.css';

export interface BomRowMeta {
    status?: SaveStatus;
    error?: string | null;
    /** "Updated existing line" and the like, from the server's outcome. */
    note?: string | null;
}

interface BomRowsProps {
    rows: BomLineDraft[];
    picks: BomMaterialPick[];
    onChange: (index: number, patch: Partial<BomLineDraft>) => void;
    /** The row's values have settled: quantity box blurred, or the material changed. */
    onCommit?: (index: number) => void;
    onAdd: () => void;
    onRemove: (index: number) => void;
    onNewMaterial?: () => void;
    /** True for lines that already exist on the server: the material is then fixed. */
    materialLocked?: (index: number) => boolean;
    meta?: (index: number) => BomRowMeta | undefined;
    rowErrors?: Record<number, string>;
    /** The recipe chips above the rows: which maker these lines belong to. */
    recipe?: {
        value: string | null;
        /** The makers that could hold a recipe of their own, labelled by name. */
        options: SelectOption[];
        onChange: (vendorId: string | null) => void;
        /** Lines each recipe holds, so the chip can say "Shimla (4)". */
        counts?: Record<string, number>;
    };
    /** Stock on hand per material, in the material's unit; shown beside each line. */
    stockByMaterial?: Map<string, number>;
    /**
     * Offered on an empty vendor recipe: start it as a copy of the brand recipe, so a
     * maker who differs in one line does not retype the other eleven.
     */
    copyShared?: { onCopy: () => void; busy: boolean; sharedCount: number };
}

const tone = (type: string): 'info' | 'warning' | 'neutral' =>
    type === 'RM' ? 'info' : type === 'PM' ? 'warning' : 'neutral';

/**
 * The "what goes into it" rows: material, quantity per one finished unit, the material's
 * own unit and type.
 *
 * Quantity is per unit of the finished product in the MATERIAL's unit, which is why the unit
 * is read from the pick and shown beside the box rather than typed. The recipe selector says
 * whose recipe the lines are: every maker (the default recipe) or one named maker, who then
 * uses only their own lines.
 */
const BomRows: React.FC<BomRowsProps> = ({
    rows,
    picks,
    onChange,
    onCommit,
    onAdd,
    onRemove,
    onNewMaterial,
    materialLocked,
    meta,
    rowErrors,
    recipe,
    stockByMaterial,
    copyShared,
}) => {
    const options = materialOptions(picks);
    const byId = new Map(picks.map((p) => [p.materialId, p]));
    const chipLabel = (value: string, label: string) => {
        const count = recipe?.counts?.[value];
        return count ? `${label} (${count})` : label;
    };

    return (
        <div className={styles.rows}>
            {recipe && (
                <div className={styles.recipe}>
                    <span className={styles.recipeLabel}>Recipe</span>
                    <SegmentedToggle
                        value={recipe.value ?? ''}
                        onChange={(v) => recipe.onChange(v ? v : null)}
                        options={[
                            { value: '', label: chipLabel('', 'Brand recipe') },
                            ...recipe.options.map((o) => ({ value: o.value, label: chipLabel(o.value, o.label) })),
                        ]}
                        height={26}
                    />
                </div>
            )}
            {rows.length === 0 && !copyShared && (
                <span className={styles.empty}>No BOM yet. Production planning cannot explode this product until it has one.</span>
            )}
            {rows.length === 0 && copyShared && (
                <div className={styles.copyShared}>
                    <span className={styles.empty}>
                        {copyShared.sharedCount > 0
                            ? 'This maker uses the brand recipe. Lines of its own replace the brand recipe for its production only.'
                            : 'No lines for this maker yet, and no brand recipe to start from.'}
                    </span>
                    {copyShared.sharedCount > 0 && (
                        <Button size="xs" variant="default" loading={copyShared.busy} onClick={copyShared.onCopy}>
                            Copy brand recipe ({copyShared.sharedCount} lines)
                        </Button>
                    )}
                </div>
            )}
            {rows.map((row, index) => {
                const pick = row.materialId ? byId.get(row.materialId) : undefined;
                const m = meta?.(index);
                const locked = materialLocked?.(index) ?? false;
                return (
                    <div key={index}>
                        <div className={styles.row}>
                            <Select
                                label="Material"
                                placeholder="Choose a material"
                                data={options}
                                value={row.materialId}
                                onChange={(v) => {
                                    onChange(index, { materialId: v });
                                    onCommit?.(index);
                                }}
                                searchable
                                disabled={locked}
                                error={rowErrors?.[index]}
                                comboboxProps={FULL_WIDTH_DROPDOWN}
                                size="xs"
                            />
                            <NumberInput
                                label="Qty per unit"
                                placeholder="e.g. 0.0065"
                                value={row.quantity}
                                onChange={(v) => onChange(index, { quantity: v === '' ? '' : Number(v) })}
                                min={0}
                                decimalScale={6}
                                allowNegative={false}
                                size="xs"
                                onBlur={() => onCommit?.(index)}
                            />
                            <div className={styles.unit}>
                                <span>{pick?.unit ?? ''}</span>
                                {pick && <StatusPill label={pick.materialType} tone={tone(pick.materialType)} />}
                                {pick && stockByMaterial && (
                                    <span className={styles.stock} title="On hand today, across warehouses">
                                        {stockByMaterial.has(pick.materialId)
                                            ? `${formatMaterialQuantity(stockByMaterial.get(pick.materialId) ?? 0, pick.unit)} on hand`
                                            : 'no stock on file'}
                                    </span>
                                )}
                            </div>
                            <ActionIcon
                                variant="subtle"
                                color="gray"
                                aria-label="Remove line"
                                className={styles.remove}
                                onClick={() => onRemove(index)}
                            >
                                <X size={14} />
                            </ActionIcon>
                            {(m?.status || m?.note) && (
                                <div className={styles.rowMeta}>
                                    {m?.status && <SaveState status={m.status} message={m.error} />}
                                    {m?.note && <span>{m.note}</span>}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
            <div className={styles.actions}>
                <button type="button" className={styles.linkButton} onClick={onAdd}>
                    + Add line
                </button>
                {onNewMaterial && (
                    <button type="button" className={styles.linkButton} onClick={onNewMaterial}>
                        + New material
                    </button>
                )}
            </div>
        </div>
    );
};

export default BomRows;
